/**
 * Address Domain Logic
 *
 * Pure functions for address matching, formatting, and normalization.
 * No external dependencies.
 */

/**
 * Common street suffix variations for normalization.
 * Maps variations to canonical form.
 */
export const STREET_SUFFIX_MAP = Object.freeze({
  'rd': 'road',
  'road': 'road',
  'st': 'street',
  'street': 'street',
  'av': 'avenue',
  'ave': 'avenue',
  'avenue': 'avenue',
  'dr': 'drive',
  'drive': 'drive',
  'ln': 'lane',
  'lane': 'lane',
  'cir': 'circle',
  'circle': 'circle',
  'ct': 'court',
  'court': 'court',
  'pl': 'place',
  'place': 'place',
  'blvd': 'boulevard',
  'boulevard': 'boulevard',
  'way': 'way',
  'ter': 'terrace',
  'terrace': 'terrace',
  'est': 'estate',
  'estate': 'estate',
  'estates': 'estates'
});

/**
 * Regex to match common street suffixes at the end of a string.
 */
export const STREET_SUFFIX_REGEX = / (rd|road|st|street|av|ave|avenue|dr|drive|ln|lane|cir|circle|ct|court|pl|place|blvd|boulevard|way|ter|terrace|est|estate|estates)$/i;

/**
 * Normalize a street name by removing common suffixes and lowercasing.
 * Useful for fuzzy matching.
 *
 * @param {string} street - Street name
 * @returns {string} Normalized street name
 */
export function normalizeStreetName(street) {
  if (!street) return '';
  return street.trim().toLowerCase().replace(STREET_SUFFIX_REGEX, '').trim();
}

/**
 * Normalize a street number for comparison.
 * Handles string/number conversion and trims whitespace.
 *
 * @param {string|number} streetNumber
 * @returns {string} Normalized street number
 */
export function normalizeStreetNumber(streetNumber) {
  if (streetNumber == null) return '';
  return String(streetNumber).trim().toLowerCase();
}

/**
 * Normalize a unit/apartment number for comparison.
 *
 * @param {string} unit
 * @returns {string} Normalized unit
 */
export function normalizeUnit(unit) {
  if (!unit) return '';
  return String(unit).trim().toLowerCase().replace(/^(apt|unit|#)\s*/i, '');
}

/**
 * Check if two addresses match.
 *
 * @param {Object} addr1 - First address
 * @param {string} [addr1.id] - Unique identifier (e.g., Google Place ID)
 * @param {string|number} [addr1.streetNumber]
 * @param {string} [addr1.street]
 * @param {string} [addr1.unit]
 * @param {Object} addr2 - Second address
 * @param {string} [addr2.id]
 * @param {string|number} [addr2.streetNumber]
 * @param {string} [addr2.street]
 * @param {string} [addr2.unit]
 * @param {Object} [options]
 * @param {boolean} [options.lenient=false] - If true, don't require units to match when one is blank
 * @param {boolean} [options.ignoreCase=true] - If true, ignore case when comparing
 * @returns {boolean} True if addresses match
 */
export function addressesMatch(addr1, addr2, options = {}) {
  const { lenient = false, ignoreCase = true } = options;

  if (!addr1 || !addr2) {
    return false;
  }

  // Perfect ID match (e.g., Google Place ID)
  if (addr1.id && addr2.id && addr1.id === addr2.id) {
    return true;
  }

  // Compare street number
  const num1 = normalizeStreetNumber(addr1.streetNumber);
  const num2 = normalizeStreetNumber(addr2.streetNumber);
  if (num1 !== num2) {
    return false;
  }

  // Compare street name
  let street1 = addr1.street || '';
  let street2 = addr2.street || '';
  if (ignoreCase) {
    street1 = street1.toLowerCase();
    street2 = street2.toLowerCase();
  }
  if (street1 !== street2) {
    return false;
  }

  // Compare unit
  const unit1 = normalizeUnit(addr1.unit);
  const unit2 = normalizeUnit(addr2.unit);

  if (unit1 === unit2) {
    return true;
  }

  // In lenient mode, match if either unit is blank
  if (lenient && (!unit1 || !unit2)) {
    return true;
  }

  return false;
}

/**
 * Format a street address (number + street + optional unit).
 *
 * @param {Object} address
 * @param {string|number} [address.streetNumber]
 * @param {string|number} [address.number]
 * @param {string} [address.street]
 * @param {string} [address.name]
 * @param {string} [address.streetAbbrev] - Abbreviated street name (preferred over street if provided)
 * @param {string} [address.suffix]
 * @param {string} [address.unit]
 * @param {Object} [options]
 * @param {boolean} [options.includeUnit=true] - Whether to include unit in output
 * @param {boolean} [options.preferAbbrev=true] - Whether to prefer abbreviated street name
 * @returns {string} Formatted street address
 */
export function formatStreetAddress(address, options = {}) {
  const { includeUnit = true, preferAbbrev = true } = options;

  if (!address) return '';

  const parts = [];

  const streetNumber = address.streetNumber || address.number;
  if (streetNumber) {
    parts.push(String(streetNumber));
  }

  const fullStreet = address.street || address.name;
  const abbreviatedStreet = address.streetAbbrev;
  const fullStreetWordCount = String(fullStreet || '').trim().split(/\s+/).filter(Boolean).length;
  const abbreviatedStreetWordCount = String(abbreviatedStreet || '').trim().split(/\s+/).filter(Boolean).length;
  const abbreviationDropsWords = fullStreetWordCount > abbreviatedStreetWordCount;
  const street = preferAbbrev && abbreviatedStreet && !abbreviationDropsWords
    ? abbreviatedStreet
    : fullStreet || abbreviatedStreet;
  if (street) {
    parts.push(street);
  }

  if (address.suffix) {
    parts.push(address.suffix);
  }

  if (includeUnit && address.unit) {
    parts.push(`#${address.unit}`);
  }

  return parts.join(' ');
}

/**
 * Format a full address (street + city + state + zip).
 *
 * @param {Object} address
 * @param {string|number} [address.streetNumber]
 * @param {string} [address.street]
 * @param {string} [address.streetAbbrev]
 * @param {string} [address.unit]
 * @param {string} [address.city]
 * @param {string} [address.state]
 * @param {string} [address.zipCode]
 * @param {string} [address.label] - If provided, use this as the full formatted address
 * @param {Object} [options]
 * @param {boolean} [options.includeUnit=true]
 * @returns {string} Formatted full address
 */
export function formatFullAddress(address, options = {}) {
  if (!address) return '';

  // If a pre-formatted label exists, use it
  if (address.label) {
    return address.label;
  }

  const parts = [];

  const street = formatStreetAddress(address, options);
  if (street) {
    parts.push(street);
  }

  if (address.city) {
    parts.push(address.city);
  }

  if (address.state) {
    parts.push(address.state);
  }

  return parts.filter(Boolean).join(', ');
}

/**
 * Compare two addresses for sorting purposes.
 * Sorts by street name first, then by street number.
 *
 * @param {Object} addr1
 * @param {Object} addr2
 * @returns {number} Negative if addr1 < addr2, positive if addr1 > addr2, 0 if equal
 */
export function compareAddresses(addr1, addr2) {
  // Handle null/undefined
  if (!addr1 && !addr2) return 0;
  if (!addr1) return 1;
  if (!addr2) return -1;

  // Compare street names
  const street1 = (addr1.street || '').toLowerCase();
  const street2 = (addr2.street || '').toLowerCase();
  const streetDiff = street1.localeCompare(street2);
  if (streetDiff !== 0) {
    return streetDiff;
  }

  // Compare street numbers (numerically if possible)
  const num1 = parseInt(addr1.streetNumber, 10) || 0;
  const num2 = parseInt(addr2.streetNumber, 10) || 0;
  if (num1 !== num2) {
    return num1 - num2;
  }

  // Compare units
  const unit1 = normalizeUnit(addr1.unit);
  const unit2 = normalizeUnit(addr2.unit);
  return unit1.localeCompare(unit2);
}

/**
 * Parse a single-line address string into components.
 * This is a simple parser for common US address formats.
 *
 * @param {string} addressString - e.g., "123 Main St, Portland, ME 04101"
 * @returns {Object} Parsed address with streetNumber, street, city, state, zipCode
 */
export function parseAddressString(addressString) {
  if (!addressString) {
    return { streetNumber: '', street: '', city: '', state: '', zipCode: '' };
  }

  const result = {
    streetNumber: '',
    street: '',
    unit: '',
    city: '',
    state: '',
    zipCode: ''
  };

  // Split by comma to get parts
  const parts = addressString.split(',').map(p => p.trim());

  if (parts.length >= 1) {
    // First part is street address
    const streetPart = parts[0];
    const streetMatch = streetPart.match(/^(\d+[-\d]*)\s+(.+?)(?:\s+((?:apt|unit|#)\s*.+))?$/i);
    if (streetMatch) {
      result.streetNumber = streetMatch[1];
      result.street = streetMatch[2];
      if (streetMatch[3]) {
        result.unit = streetMatch[3].replace(/^(apt|unit|#)\s*/i, '');
      }
    } else {
      result.street = streetPart;
    }
  }

  if (parts.length >= 2) {
    // Second part is city
    result.city = parts[1];
  }

  if (parts.length >= 3) {
    // Third part is state + zip
    const stateZipMatch = parts[2].match(/^([A-Z]{2})\s*(\d{5}(?:-\d{4})?)?$/i);
    if (stateZipMatch) {
      result.state = stateZipMatch[1].toUpperCase();
      result.zipCode = stateZipMatch[2] || '';
    } else {
      result.state = parts[2];
    }
  }

  return result;
}
